<template>
  <div>
    <b-button
        size="sm"
        variant="outline-secondary"
        @click="showModal"
        class="btn-action"
        v-b-tooltip.hover title="View Logs"
    >
        <i class="fa fa-history"></i>
    </b-button>

    <b-modal
        ref="log-modal"
        size="xl"
        hide-footer
        no-close-on-esc
        no-close-on-backdrop
        @shown="initModal"
        @hidden="resetModal"
        header-bg-variant="primary"
        header-text-variant="light"
    >
      <template slot="modal-title">
          <div class="d-flex align-items-center">
              <i class="fa fa-history mr-2"></i>
              <span>{{ displayTitle }}</span>
          </div>
      </template>

      <div class="d-flex justify-content-end mb-3 align-items-center">
         <div>
           <b-button variant="outline-success" size="sm" class="mr-2" @click="exportLogs" :disabled="isExporting">
             <i class="fa fa-file-excel-o"></i> {{ isExporting ? 'Exporting...' : 'Export' }}
           </b-button>
           <b-button variant="outline-secondary" size="sm" @click="fetchLogs">
             <i class="fa fa-refresh"></i> Refresh
           </b-button>
         </div>
      </div>

      <CustomLoader v-if="isLoading" :width="70" />
      
      <div class="table-responsive">
        <b-table
          :items="logs"
          :fields="fields"
          show-empty
          responsive
          hover
          head-variant="light"
          class="mb-0 custom-table"
        >
          <template slot="empty">
            <div class="text-center text-muted my-4">
                <p>No log records found.</p>
            </div>
          </template>

          <template slot="Sn" slot-scope="row">
            {{ (currentPage - 1) * perPage + row.index + 1 }}
          </template>

          <template slot="pl_log_created_at" slot-scope="row">
            {{ formatDate(row.item.pl_log_created_at) }}
          </template>
          <template slot="employeeName" slot-scope="row">
            {{ row.item.pl_log_cu_relation ? row.item.pl_log_cu_relation.employeeName : '-' }}
          </template>
        </b-table>
      </div>

      <b-row class="mt-3 align-items-center">
          <b-col md="6">
              <div class="text-muted small" v-if="totalRows > 0">
                   Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage * perPage, totalRows) }} of {{ totalRows }} entries
               </div>
          </b-col>
          <b-col md="6">
               <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  align="right"
                  class="mb-0 custom-pagination"
                  @change="onPageChange"
                  pills
                  size="sm"
              />
          </b-col>
      </b-row>
      
      <b-row class="mt-3 border-top pt-3">
          <b-col cols="12" class="text-center">
               <b-button variant="outline-danger" size="sm" @click="hideModal">Close</b-button>
          </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import ProfitLossService from "../../services/ProfitLossService";
import CustomLoader from "../common/customLoader.vue";
import moment from "moment";
import { exportProfitLossLogToExcel } from "../../helper/ExcelExportHelper";

export default {
  name: "ProfitLossLogModal",
  components: {
    CustomLoader
  },
  props: {
    PMC_title: {
      type: String,
      default: ""
    },
    PMC_JOBID: {
      type: [Number, String],
      required: true
    },
    PMC_JOBNO: {
        type: String,
        default: ""
    },
    clientName: {
        type: String,
        default: ""
    }
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,
      logs: [],
      fields: [
        { key: "Sn", label: "SN", thClass: 'text-center', tdClass: 'text-center' },
        { key: "pl_field_changed", label: "Field Changed", sortable: true },
        { key: "pl_old_value", label: "Old Value" },
        { key: "pl_new_value", label: "New Value" },
        { key: "employeeName", label: "Changed By" },
        { key: "pl_log_created_at", label: "Date & Time", sortable: true }
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0
    };
  },
  computed: {
    displayTitle() {
        let titleParts = [this.PMC_title || 'Log Records'];
        if (this.PMC_JOBNO) titleParts.push(`(${this.PMC_JOBNO})`);
        if (this.clientName) titleParts.push(`(${this.clientName})`);
        return titleParts.join(' ');
    }
  },
  methods: {
    showModal() {
      // alert(this.PMC_title);
      this.initModal();
      this.$refs['log-modal'].show();
    },
    hideModal() {
        this.$refs['log-modal'].hide();
    },
    initModal() {
        this.currentPage = 1;
        this.fetchLogs();
    },
    onPageChange(page) {
        this.currentPage = page;
        this.fetchLogs();
    },
    async fetchLogs() {
      if (!this.PMC_JOBID) return;
      
      this.isLoading = true;
      
      try {
        const payload = {
            fk_pl_id: this.PMC_JOBID,
            pageNo: this.currentPage,
            pageSize: this.perPage, 
            isAll: 0 
        };
        
        const response = await ProfitLossService.getLogRecords(payload);
        if (response.data.status === 200 || response.data.type === 'success') {
            const responseData = response.data.data;
            
            if (Array.isArray(responseData)) {
                 this.logs = responseData;
                 this.totalRows = responseData.length;
            } else if (responseData && responseData.data) {
                 this.logs = responseData.data; 
                 this.totalRows = responseData.count || responseData.totalCount || 0;
            } else {
                 this.logs = [];
                 this.totalRows = 0;
            }
        }
      } catch (error) {
        console.error("Error fetching logs:", error);
         this.$bvToast.toast('Failed to fetch logs', {
            title: 'Error',
            variant: 'danger',
            solid: true
        });
      } finally {
        this.isLoading = false;
      }
    },
    async exportLogs() {
        this.isExporting = true;
        try {
            const payload = {
                fk_pl_id: this.PMC_JOBID,
                pageNo: 1,
                pageSize: 100000, 
                isAll: 0
            };
            const response = await ProfitLossService.getLogRecords(payload);
            let exportData = [];

             if (response.data.status === 200 || response.data.type === 'success') {
                const responseData = response.data.data;
                 if (Array.isArray(responseData)) {
                     exportData = responseData;
                 } else if (responseData && responseData.data) {
                     exportData = responseData.data;
                 }
             }

             if (exportData.length > 0) {
                 exportProfitLossLogToExcel(exportData, `Logs_${this.PMC_JOBNO}.xlsx`);
             } else {
                 this.$bvToast.toast('No data to export', {
                    title: 'Info',
                    variant: 'info',
                    solid: true
                });
             }

        } catch (error) {
             console.error("Export Error:", error);
             this.$bvToast.toast('Failed to export logs', {
                title: 'Error',
                variant: 'danger',
                solid: true
            });
        } finally {
             this.isExporting = false;
        }
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD/MM/YYYY HH:mm:ss");
    },
    resetModal() {
        this.logs = [];
        this.currentPage = 1;
        this.totalRows = 0;
    }
  }
};
</script>

<style scoped>
.custom-table {
    font-size: 0.9rem;
}
</style>



// WEBPACK FOOTER //
// ProfitLossLogModal.vue?740429dc